/**
 * The catalog, as a hook (RESP2d).
 *
 * A thin subscriber over `activityCategoryCatalogStore` — every derived
 * shape below is memoised, so a table of 200 rows that all call this
 * pays for one map, not two hundred.
 *
 * The two lists are deliberately separate:
 *
 * * `allCategories` — everything, including retired areas. What a LIST
 *   needs, because a task filed last month must still be nameable.
 * * `activeCategories` — what a FORM may offer.
 *
 * Collapsing them into one filtered list is the bug this shape prevents:
 * a display surface would then show "unknown area" the moment somebody
 * deactivated a category, which is precisely the history-rewriting
 * RESP2b/RESP2c were built to avoid.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

import {
  getCatalogState,
  invalidateActivityCategoryCatalog,
  loadCatalog,
  subscribeToCatalog,
} from "./activityCategoryCatalogStore";

/** Shown wherever a row carries no category — never a raw key. */
export const UNASSIGNED_CATEGORY_LABEL = "Nicht zugewiesen";

/**
 * Shown when a row names an area the catalog cannot resolve.
 *
 * Never the slug. A technical key is not a name, and printing one both
 * looks broken and invites somebody to type it somewhere. This state
 * should be unreachable — the composite foreign key guarantees the area
 * exists — so seeing it means the catalog failed to load, which is
 * worth saying plainly rather than papering over.
 */
export const UNKNOWN_CATEGORY_LABEL = "Bereich nicht verfügbar";

export default function useActivityCategoryCatalog() {
  const state = useSyncExternalStore(
    subscribeToCatalog,
    getCatalogState,
    getCatalogState
  );

  useEffect(() => {
    // No-op when another consumer already loaded it — that is the store's
    // job, and the reason a page with six consumers makes one request.
    void loadCatalog();
  }, []);

  const allCategories = state.items;

  const activeCategories = useMemo(
    () => allCategories.filter((c) => c.is_active !== false),
    [allCategories]
  );

  const categoryByKey = useMemo(() => {
    const map = new Map();
    for (const c of allCategories) map.set(c.activity_category, c);
    return map;
  }, [allCategories]);

  const getCategoryMeta = useCallback(
    (key) => (key ? categoryByKey.get(key) || null : null),
    [categoryByKey]
  );

  /**
   * What a select may offer, given what it currently holds.
   *
   * The active ones, plus the current value if it is a retired area —
   * so an edit form opens showing the row's own category instead of a
   * blank select that would silently reclassify it on save. The retired
   * value is present as the CURRENT value only; picking a different one
   * and coming back is not possible, which is the RESP2d rule.
   */
  const getSelectableCategories = useCallback(
    (currentKey) => {
      if (!currentKey) return activeCategories;
      const current = categoryByKey.get(currentKey);
      if (!current || current.is_active !== false) return activeCategories;
      // Filtered from the full list rather than prepended, so a retired
      // area keeps its place in the organisation's own ordering. A
      // stored value jumping to the top of the list would look like a
      // different kind of entry than it is.
      return allCategories.filter(
        (c) => c.is_active !== false || c.activity_category === currentKey
      );
    },
    [activeCategories, allCategories, categoryByKey]
  );

  return {
    allCategories,
    activeCategories,
    categoryByKey,
    getCategoryMeta,
    getSelectableCategories,
    loading: state.status === "loading" || state.status === "idle",
    error: state.status === "error" ? state.error : null,
    refresh: invalidateActivityCategoryCatalog,
  };
}
